import {
	FormControl, FormHelperText,
	Grid,
	MenuItem,
	Select, TextareaAutosize,
	Typography
} from '@material-ui/core';
import { CloudUpload, FindInPage, Refresh } from '@material-ui/icons';
import { useFormik } from 'formik';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomButton, { ButtonTypeEnum } from '../../../components/CustomButton/custom-button.component';
import Form from '../../../components/Form/Form';
import { SharedService } from '../../../services/shared.service';
import { carriers } from '../../../shared/constants';
import { CarrierTypeEnum, EFileAnalyzerTypeEnum } from '../../../shared/enum';

const defaultVersions: Record<CarrierTypeEnum, Record<EFileAnalyzerTypeEnum, string | undefined | null>> = {
	UPS: { PLD: 'v0200', PSE: '', SSF: '' },
	USPS: { PSE: 'v1', SSF: 'v020', PLD: '' },
};

export interface IEFileAnalyzerFormValues {
	carrierType: CarrierTypeEnum;
	analyzerType: EFileAnalyzerTypeEnum;
	fileContent: string;
}

const getCarrierAnalyzers = (carrierType: CarrierTypeEnum): EFileAnalyzerTypeEnum[] =>
	carrierType ? carriers.filter(c => c.type === carrierType)[0].analyzers : [];

const EFileAnalyzerForm: React.FC<{ onSubmit: Function; onReset: Function; disableFormInput: boolean }> = props => {
	const { t } = useTranslation();

	const formik = useFormik<IEFileAnalyzerFormValues>({
		initialValues: {
			carrierType: CarrierTypeEnum.USPS,
			analyzerType: EFileAnalyzerTypeEnum.SSF,
			fileContent: '',
		},
		onSubmit: async values => {
			try {
				setDisabled(true);
				await props.onSubmit(values);
				formik.setSubmitting(false);
				setDisabled(false);
			} catch (e) {
				setDisabled(false);
				formik.setSubmitting(false);
			}
		},
		onReset: () => {
			props.onReset();
			formik.setFieldValue('isSnippet', false);
		},
	});

	const [disabled, setDisabled] = useState<boolean>(props.disableFormInput);

	// open file dialog
	const showFileDialog = useCallback(
		(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			e.preventDefault();

			new SharedService().showFileDialog(false, '', async files => {
				const [file] = files;
				const reader = new FileReader();
				reader.addEventListener('load', x => {
					const content = x.target?.result;
					formik.setFieldValue('fileContent', content);
				});
				reader.readAsText(file);
			});
		},
		[formik]
	);

	return (
		<Form onSubmit={formik.handleSubmit}>
			<Grid container direction='column' spacing={3}>
				<Grid container item alignItems='center'>
					<Grid item xs={12} md={3}>
						<Typography variant='subtitle2' color='inherit'>
						{t('label-carrier-type')}
						</Typography>
					</Grid>
					<Grid container item alignItems='center' xs={12} md={9}>
						<Select
							fullWidth
							value={formik.values.carrierType}
							name='carrierType'
							onChange={e => {
								formik.setFieldValue('carrierType', e.target.value);
								formik.setFieldValue('analyzerType', getCarrierAnalyzers(e.target.value as CarrierTypeEnum)[0]);
							}}
							disabled={disabled || props.disableFormInput}
						>
							{carriers.map(({ type }) => (
								<MenuItem key={type} value={type}>
									{type}
								</MenuItem>
							))}
						</Select>
					</Grid>
				</Grid>
				<Grid container item alignItems='center'>
					<Grid item xs={12} md={3}>
						<Typography variant='subtitle2' color='inherit'>
						{t('label-file-type')}
						</Typography>
					</Grid>
					<Grid container item alignItems='center' xs={12} md={9}>
						<Select
							fullWidth
							value={formik.values.analyzerType}
							name='analyzerType'
							onChange={e => formik.setFieldValue('analyzerType', e.target.value)}
							disabled={disabled || props.disableFormInput}
						>
							{getCarrierAnalyzers(formik.values.carrierType).map(analyzer => (
								<MenuItem key={analyzer} value={analyzer}>
									{analyzer}
								</MenuItem>
							))}
						</Select>
					</Grid>
				</Grid>
				<Grid container item alignItems='center'>
					<Grid item xs={12} md={3}>
						<Typography variant='subtitle2' color='inherit'>
							{t('label-efile-content')}
						</Typography>
					</Grid>
					<Grid container item direction='column' spacing={2} xs={12} md={9}>
						<Grid item xs={12}>
							<FormControl fullWidth variant='standard'>
								<TextareaAutosize
									minRows={10}
									maxRows={10}
									onChange={formik.handleChange}
									name='fileContent'
									style={{
										minHeight: 200,
										maxHeight: 300,
										height: 200,
										resize: 'vertical',
										width: '100%',
									}}
									disabled={disabled || props.disableFormInput}
									value={formik.values.fileContent || ''}
									placeholder={t('placeholder-efile-content')}
									aria-describedby='component-helper-text'
								/>
								<FormHelperText id='component-helper-text'>
									<Typography>
										{t('label-default-efile-version')} {defaultVersions[formik.values.carrierType][formik.values.analyzerType]}
									</Typography>
								</FormHelperText>
								<FormHelperText id='component-helper-text'>
									<Typography color='error'>{formik.errors['fileContent']}</Typography>
								</FormHelperText>
							</FormControl>
						</Grid>
					</Grid>
				</Grid>
				<Grid container item justifyContent='center' spacing={2}>
					<Grid item>
						<CustomButton
							onClick={showFileDialog}
							startIcon={<CloudUpload />}
							buttonText={t('label-browse')}
							disabled={!!formik.values.fileContent || disabled || props.disableFormInput}
						/>
					</Grid>
					<Grid item>
						<CustomButton
							type={ButtonTypeEnum.Submit}
							disabled={!formik.values.fileContent || disabled || props.disableFormInput}
							startIcon={<FindInPage />}
							buttonText={t('label-analyze-file')}
						/>
					</Grid>
					<Grid item>
						<CustomButton
							type={ButtonTypeEnum.Reset}
							onClick={() => {
								formik.resetForm();
							}}
							startIcon={<Refresh />}
							buttonText={t('label-reset')}
							disabled={!formik.dirty || disabled}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Form>
	);
};

export default EFileAnalyzerForm;
